/**
 * Candidate people for a picker, derived from the chat channel list that
 * `useChatRealtime()` already holds in memory (`store/chatStore.js` →
 * `channels`, filled by `chatService.listChannels()` on auth).
 *
 * DISCLOSURE POSITION — read before changing anything here.
 * These are exactly the people already listed, by name and avatar, in the chat
 * sidebar and its member popovers. Nothing in this module makes anyone
 * findable who was not findable before, and nothing here may ever grow into a
 * directory: the server states the rule outright in
 * `backend_phoenix/lib/backend_pheonix/chat.ex` — *"There is deliberately no
 * substring/name search: the directory must not be browsable."* Filtering here
 * is LOCAL, over a list the client already has; it never becomes a request.
 * `src/tests/calendarInviteeRoster.test.js` N2 is the control that fails if
 * someone widens this into discovery.
 *
 * Pure: takes channels, returns rows. No store reads, no network, no JSX.
 */

/** Channel kinds whose members count as people you already converse with.
 *  Owner decision D2 (2026-08-31): DMs, groups AND channel rooms. */
export const ROSTER_KINDS = ['dm', 'group', 'channel'];

/**
 * Build the candidate list from the in-memory chat channel list.
 *
 * @param {Array} channels  chatStore `channels` (each `{ kind, members: [{ user }] }`)
 * @param {{ currentUserId?: string|number, kinds?: string[] }} opts
 * @returns {Array<{id: string, name: string, email: string}>}
 *   deduped by `String(id)`, self excluded, sorted by `(name || email)`.
 */
export function rosterFromChannels(channels, { currentUserId, kinds = ROSTER_KINDS } = {}) {
  const allowed = new Set(kinds);
  const byId = new Map();

  for (const channel of Array.isArray(channels) ? channels : []) {
    if (!channel || !allowed.has(channel.kind)) continue;
    for (const member of Array.isArray(channel.members) ? channel.members : []) {
      const user = member?.user;
      const rawId = user?.id ?? member?.user_id;
      if (rawId == null) continue;
      const id = String(rawId);
      // Self is never an invitee: the organizer is on the event already, and
      // the server's own lookup excludes the caller (`u.id != ^me`).
      if (currentUserId != null && id === String(currentUserId)) continue;
      if (byId.has(id)) continue;
      byId.set(id, { id, name: user?.name || '', email: user?.email || '' });
    }
  }

  return [...byId.values()].sort((a, b) =>
    (a.name || a.email).localeCompare(b.name || b.email)
  );
}

/**
 * Case-insensitive substring over name AND email — LOCAL ONLY, over rows the
 * client already has. This is not a search; it is a filter.
 */
export function filterRoster(rows, query) {
  const q = (query || '').trim().toLowerCase();
  const list = Array.isArray(rows) ? rows : [];
  if (!q) return list;
  return list.filter(
    (r) =>
      (r.name || '').toLowerCase().includes(q) || (r.email || '').toLowerCase().includes(q)
  );
}
